(function () {
  'use strict';

  if (customElements.get('product-discount-offers')) return;

  class ProductDiscountOffers extends HTMLElement {
    connectedCallback() {
      if (this._offerListenersAttached) return;
      this._offerListenersAttached = true;
      this.dataset.initialized = 'true';
      this.status = this.querySelector('[data-offer-status]');
      this.rewardTitle = this.querySelector('[data-reward-title]');
      this.rewardSubtitle = this.querySelector('[data-reward-subtitle]');
      this.rewardBadge = this.querySelector('[data-reward-badge]');
      this.currencyGlyphTemplate = this.querySelector('[data-reward-currency-glyph]');
      this.progress = this.querySelector('[data-reward-progress]');
      this.cards = Array.from(this.querySelectorAll('[data-offer-card]')).sort(
        (a, b) => Number(a.dataset.minimumCents || 0) - Number(b.dataset.minimumCents || 0)
      );
      this.milestones = Array.from(this.querySelectorAll('[data-reward-milestone]'));
      this.variants = this.readVariants();
      this.cartSubtotal = 0;
      this.busy = false;

      this.boundClick = this.onClick.bind(this);
      this.boundProductChange = this.onProductChange.bind(this);
      this.boundCartUpdate = this.onCartUpdate.bind(this);
      this.addEventListener('click', this.boundClick);
      document.addEventListener('change', this.boundProductChange);
      document.addEventListener('cart:updated', this.boundCartUpdate);
      document.addEventListener('variant:change', this.boundProductChange);
      this.attachCartUpdateSubscription();

      this.restoreSelection();
      this.refreshCartState();
    }

    disconnectedCallback() {
      if (!this._offerListenersAttached) return;
      this.removeEventListener('click', this.boundClick);
      document.removeEventListener('change', this.boundProductChange);
      document.removeEventListener('cart:updated', this.boundCartUpdate);
      document.removeEventListener('variant:change', this.boundProductChange);
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
      window.clearTimeout(this.cartUpdateSubscriptionTimer);
      this.cartUpdateSubscriptionTimer = null;
      window.clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = null;
      this.activeCopyButton = null;
      this._offerListenersAttached = false;
      this.dataset.initialized = 'false';
    }

    get storageKey() {
      return this.dataset.storageKey || 'alasil_selected_discount_v1';
    }

    readVariants() {
      const script = this.querySelector('[data-offer-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    onClick(event) {
      const copyButton = event.target.closest('[data-pdp-copy-code]');
      if (copyButton) {
        event.preventDefault();
        this.trackOffer('coupon_clicked', copyButton.dataset.pdpCopyCode, { source: 'product_copy' });
        this.copyPdpCode(copyButton);
        return;
      }

      const card = event.target.closest('[data-offer-card]');
      const applyButton = event.target.closest('[data-offer-apply]');
      if (!card || !applyButton || this.busy) return;
      const minimum = Number(card.dataset.minimumCents || 0);
      if (card.classList.contains('is-locked') || this.cartSubtotal < minimum) {
        this.showStatus(this.dataset.lockedText || 'Add more to unlock this reward.', 'error');
        return;
      }
      if (card.classList.contains('is-selected')) this.removeOffer(card);
      else {
        this.trackOffer('coupon_clicked', card.dataset.discountCode, { source: 'product_offer' });
        this.selectOffer(card);
      }
    }

    onProductChange(event) {
      const productForm = event.target && event.target.closest
        ? event.target.closest('product-form, form[action*="/cart/add"], product-info')
        : null;
      if (!productForm && event.type === 'change') return;
      window.requestAnimationFrame(() => this.updateRewardState());
    }

    attachCartUpdateSubscription() {
      if (this.cartUpdateUnsubscriber) return;

      if (
        typeof subscribe !== 'function' ||
        typeof PUB_SUB_EVENTS === 'undefined' ||
        !PUB_SUB_EVENTS.cartUpdate
      ) {
        window.clearTimeout(this.cartUpdateSubscriptionTimer);
        this.cartUpdateSubscriptionTimer = window.setTimeout(
          () => this.attachCartUpdateSubscription(),
          100
        );
        return;
      }

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundCartUpdate);
    }

    onCartUpdate(event) {
      const cart = event && event.cartData
        ? event.cartData
        : event && event.detail && event.detail.cart
          ? event.detail.cart
          : null;

      if (cart) {
        this.applyCartState(cart);
        return;
      }

      this.refreshCartState();
    }

    applyCartState(cart) {
      if (!cart || this.busy) return;
      this.cartSubtotal = Number(cart.items_subtotal_price || cart.total_price || 0);
      this.syncSelection(cart);
      this.updateRewardState();
    }

    async refreshCartState() {
      try {
        const cart = typeof window.getCartSnapshot === 'function'
          ? await window.getCartSnapshot()
          : typeof window.whenCartSnapshotReady === 'function'
            ? await window.whenCartSnapshotReady()
            : null;
        this.applyCartState(cart);
      } catch (error) {
        this.updateRewardState();
      }
    }

    getProjectedSubtotal() {
      const formId = `product-form-${this.dataset.sectionId}`;
      const form = document.querySelector(
        `#${formId}, product-form[data-section-id="${this.dataset.sectionId}"] form, form[action*="/cart/add"]`
      );
      const productInfo = this.closest('product-info') || document.querySelector('product-info');
      const variantInput = form && form.querySelector('[name="id"]');
      const quantityInput = document.querySelector(`[name="quantity"][form="${formId}"]`) ||
        (form && form.querySelector('[name="quantity"]'));
      const appleCareInput = (productInfo && productInfo.querySelector('[data-applecare-input]')) ||
        document.querySelector('[data-applecare-input]');
      const selectedId = Number(variantInput && variantInput.value);
      const variant = this.variants.find((item) => Number(item.id) === selectedId) || this.variants[0];
      const quantity = Math.max(1, Number(quantityInput && quantityInput.value) || 1);
      const productPrice = variant ? Number(variant.price || 0) : 0;
      const appleCarePrice = appleCareInput && appleCareInput.checked
        ? Number(appleCareInput.dataset.applecarePrice || 0)
        : 0;

      return this.cartSubtotal + ((productPrice + appleCarePrice) * quantity);
    }

    updateRewardState() {
      if (!this.cards.length) return;
      const projectedSubtotal = this.getProjectedSubtotal();
      const maxThreshold = Math.max(...this.cards.map((card) => Number(card.dataset.minimumCents || 0)), 1);
      const eligibleCards = this.cards.filter((card) => this.cartSubtotal >= Number(card.dataset.minimumCents || 0));
      const projectedCards = this.cards.filter((card) => projectedSubtotal >= Number(card.dataset.minimumCents || 0));
      const bestEligible = eligibleCards[eligibleCards.length - 1] || null;
      const bestProjected = projectedCards[projectedCards.length - 1] || null;
      const nextCartCard = this.cards.find((card) => this.cartSubtotal < Number(card.dataset.minimumCents || 0)) || null;
      let visibleCardCount = 0;

      if (this.rewardBadge) {
        this.rewardBadge.textContent = bestEligible
          ? this.getTranslation('rewardUnlockedLabel', 'Reward unlocked')
          : bestProjected
            ? this.getTranslation('afterAddingLabel', 'After adding')
            : this.getTranslation('lockedLabel', 'Locked');
      }

      this.cards.forEach((card) => {
        const minimum = Number(card.dataset.minimumCents || 0);
        const eligible = this.cartSubtotal >= minimum;
        const availableAfterAdding = !eligible && projectedSubtotal >= minimum;
        const eligibleWithProduct = eligible || availableAfterAdding;
        const recommended = card === bestEligible;
        card.hidden = !eligibleWithProduct;
        card.setAttribute('aria-hidden', String(!eligibleWithProduct));
        if (eligibleWithProduct) visibleCardCount += 1;
        card.classList.toggle('is-unlocked', eligible);
        card.classList.toggle('is-projected', availableAfterAdding);
        card.classList.toggle('is-locked', !eligible);
        card.classList.toggle('is-recommended', recommended);
        card.querySelectorAll('[data-offer-apply]').forEach((button) => {
          button.disabled = !eligible;
          button.setAttribute('aria-disabled', String(!eligible));
        });
        const state = card.querySelector('[data-tier-state]');
        if (state) {
          state.textContent = recommended
            ? this.getTranslation('bestAvailable', 'Best available')
            : eligible
              ? this.getTranslation('unlockedLabel', 'Unlocked')
              : availableAfterAdding
                ? this.getTranslation('afterAddingLabel', 'After adding')
              : this.getTranslation('lockedLabel', 'Locked');
        }
        this.updateCardAction(card);
      });

      this.hidden = visibleCardCount === 0;
      this.setAttribute('aria-hidden', String(visibleCardCount === 0));

      this.milestones.forEach((milestone) => {
        const reached = projectedSubtotal >= Number(milestone.dataset.minimumCents || 0);
        milestone.classList.toggle('is-reached', reached);
      });

      if (this.progress) {
        this.progress.style.width = `${Math.min(100, Math.max(2, projectedSubtotal / maxThreshold * 100))}%`;
      }

      if (!bestEligible && bestProjected) {
        this.setRewardCopy(
          this.dataset.addItemTemplate,
          this.dataset.addItemSubtitle || 'Add the item to your cart before applying the code.',
          { saving: Number(bestProjected.dataset.savingCents || 0) }
        );
      } else if (!bestEligible && nextCartCard) {
        const remaining = Number(nextCartCard.dataset.minimumCents || 0) - this.cartSubtotal;
        this.setRewardCopy(
          this.dataset.addMoreTemplate,
          this.dataset.addItemSubtitle || 'Add the item to your cart before applying the code.',
          {
            amount: remaining,
            saving: Number(nextCartCard.dataset.savingCents || 0)
          }
        );
      } else if (bestEligible && bestProjected && bestProjected !== bestEligible) {
        this.setRewardCopy(
          this.dataset.unlockedTemplate,
          this.dataset.addItemTemplate,
          { saving: Number(bestEligible.dataset.savingCents || 0) },
          { saving: Number(bestProjected.dataset.savingCents || 0) }
        );
      } else if (bestEligible && nextCartCard) {
        const remaining = Number(nextCartCard.dataset.minimumCents || 0) - this.cartSubtotal;
        this.setRewardCopy(
          this.dataset.unlockedTemplate,
          this.dataset.nextTemplate,
          { saving: Number(bestEligible.dataset.savingCents || 0) },
          {
            amount: remaining,
            saving: Number(nextCartCard.dataset.savingCents || 0)
          }
        );
      } else if (bestEligible) {
        this.setRewardCopy(
          this.dataset.bestTemplate,
          this.getTranslation('unlockedLabel', 'Unlocked'),
          { saving: Number(bestEligible.dataset.savingCents || 0) }
        );
      }
    }

    setRewardCopy(title, subtitle, titleValues = {}, subtitleValues = {}) {
      this.renderRewardText(this.rewardTitle, title, titleValues);
      this.renderRewardText(this.rewardSubtitle, subtitle, subtitleValues);
    }

    renderRewardText(target, template, values = {}) {
      if (!target) return;
      const text = template || '';
      const fragment = document.createDocumentFragment();
      const tokenPattern = /__(SAVING|AMOUNT)__/g;
      let cursor = 0;
      let match;

      while ((match = tokenPattern.exec(text)) !== null) {
        if (match.index > cursor) fragment.append(document.createTextNode(text.slice(cursor, match.index)));
        const key = match[1].toLowerCase();
        fragment.append(this.createMoneyNode(Number(values[key] || 0)));
        cursor = match.index + match[0].length;
      }

      if (cursor < text.length) fragment.append(document.createTextNode(text.slice(cursor)));
      target.replaceChildren(fragment);
    }

    createMoneyNode(cents) {
      const currency = this.dataset.currency || 'AED';
      const amount = this.formatAmount(cents);
      const wrapper = document.createElement('span');
      wrapper.className = 'product-discount-offers__money product-discount-offers__summary-money';
      wrapper.setAttribute('aria-label', `${currency} ${amount}`);

      const visual = document.createElement('span');
      visual.className = 'product-discount-offers__money-visual';
      visual.setAttribute('aria-hidden', 'true');
      if (this.currencyGlyphTemplate) visual.append(this.currencyGlyphTemplate.content.cloneNode(true));

      const amountNode = document.createElement('span');
      amountNode.textContent = amount;
      visual.append(amountNode);
      wrapper.append(visual);
      return wrapper;
    }

    formatAmount(cents) {
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      try {
        return new Intl.NumberFormat(locale, {
          maximumFractionDigits: cents % 100 === 0 ? 0 : 2
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toLocaleString();
      }
    }

    getTranslation(key, fallback) {
      return this.dataset[key] || fallback;
    }

    trackOffer(eventName, code, fields = {}) {
      if (typeof window.publishAlasilEvent !== 'function') return;
      window.publishAlasilEvent('alasil:storefront_interaction', Object.assign({
        event_name: eventName, coupon: String(code || '').trim(), source: 'product_offer'
      }, fields));
    }

    appliedDiscountAmount(cart, code) {
      // Only attribute allocations belonging to this code, never the cart's
      // total_discount (which can include unrelated automatic discounts).
      const matches = (title) => String(title || '').toUpperCase() === code.toUpperCase();
      let cents = 0;
      let found = false;
      (cart.cart_level_discount_applications || []).forEach((application) => {
        if (matches(application.title) && Number.isFinite(Number(application.total_allocated_amount))) {
          cents += Number(application.total_allocated_amount);
          found = true;
        }
      });
      (cart.items || []).forEach((item) => (item.line_level_discount_allocations || []).forEach((allocation) => {
        if (matches(allocation.discount_application && allocation.discount_application.title) && Number.isFinite(Number(allocation.amount))) {
          cents += Number(allocation.amount);
          found = true;
        }
      }));
      return found ? { discount: cents / 100 } : {};
    }

    async selectOffer(card) {
      const code = (card.dataset.discountCode || '').trim();
      if (!code) return;
      this.busy = true;
      this.cards.forEach((item) => item.classList.remove('is-loading'));
      card.classList.add('is-loading');
      this.showStatus(this.dataset.applyingText || 'Applying offer...', '');

      try {
        const data = await this.updateCartDiscount(code);
        const discounts = Array.isArray(data.discount_codes) ? data.discount_codes : [];
        const matchingCode = discounts.find((discount) =>
          discount && String(discount.code || '').toUpperCase() === code.toUpperCase()
        );
        if (!matchingCode || matchingCode.applicable !== true) throw new Error('discount_not_applicable');

        this.trackOffer('coupon_applied', code, this.appliedDiscountAmount(data, code));
        this.markSelected(card);
        this.persistSelection(code);
        await this.renderCartSections(data);
        this.cartSubtotal = Number(data.items_subtotal_price || data.total_price || this.cartSubtotal);
        this.updateRewardState();
        this.showStatus(this.dataset.appliedText || 'Offer applied to your cart.', 'success');
        this.dispatchDiscountChange({ code, applicable: true });
      } catch (error) {
        this.showStatus(this.dataset.errorText || 'This offer could not be applied.', 'error');
      } finally {
        card.classList.remove('is-loading');
        this.busy = false;
      }
    }

    async removeOffer(card) {
      this.busy = true;
      card.classList.add('is-loading');
      this.showStatus(this.dataset.removingText || 'Removing offer...', '');

      try {
        const data = await this.updateCartDiscount('');
        this.clearSelection();
        this.persistSelection('');
        await this.renderCartSections(data);
        this.cartSubtotal = Number(data.items_subtotal_price || data.total_price || this.cartSubtotal);
        this.updateRewardState();
        this.showStatus(this.dataset.removedText || 'Offer removed from your cart.', 'success');
        this.dispatchDiscountChange({ code: '', applicable: false, removed: true });
      } catch (error) {
        this.showStatus(this.dataset.errorText || 'This offer could not be updated.', 'error');
      } finally {
        card.classList.remove('is-loading');
        this.busy = false;
      }
    }

    async updateCartDiscount(code) {
      const url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

      const response = await window.cartMutationFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: code,
          sections: this.getCartSectionIds(),
          sections_url: window.location.pathname
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.errors) throw new Error('discount_update_failed');
      return data;
    }

    dispatchDiscountChange(detail) {
      this.dispatchEvent(new CustomEvent('discount:changed', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }

    getCartSectionIds() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const ids = cartDrawer.getSectionsToRender()
          .map((section) => section.section || section.id)
          .filter(Boolean);
        if (ids.length) return Array.from(new Set(ids));
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    async renderCartSections(cartState) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
      if (cartState && cartState.sections && cartState.sections['cart-drawer']) {
        cartState.skipDrawerOpen = true;
        cartDrawer.renderContents(cartState);
        return;
      }
      if (typeof window.refreshCartDrawerState === 'function') {
        await window.refreshCartDrawerState({ skipDrawerOpen: true });
      }
    }

    markSelected(selectedCard) {
      this.cards.forEach((card) => {
        const selected = card === selectedCard;
        card.classList.toggle('is-selected', selected);
        card.querySelectorAll('[data-offer-apply]').forEach((button) => {
          button.setAttribute('aria-pressed', String(selected));
          button.setAttribute('aria-label', selected ? button.dataset.removeLabel : button.dataset.selectLabel);
        });
        this.updateCardAction(card);
      });
    }

    updateCardAction(card) {
      const label = card.querySelector('[data-offer-action-label]');
      if (!label) return;
      if (card.classList.contains('is-selected')) label.textContent = this.getTranslation('appliedLabel', 'Applied');
      else if (card.classList.contains('is-locked')) label.textContent = this.getTranslation('lockedLabel', 'Locked');
      else label.textContent = this.getTranslation('applyLabel', 'Apply');
    }

    clearSelection() {
      this.cards.forEach((card) => {
        card.classList.remove('is-selected');
        card.querySelectorAll('[data-offer-apply]').forEach((button) => {
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', button.dataset.selectLabel);
        });
        this.updateCardAction(card);
      });
    }

    restoreSelection() {
      let selectedCode = '';
      try { selectedCode = localStorage.getItem(this.storageKey) || ''; } catch (error) {}
      const card = this.cards.find((item) =>
        String(item.dataset.discountCode || '').toUpperCase() === selectedCode.toUpperCase()
      );
      if (card) this.markSelected(card);
    }

    syncSelection(cart) {
      const discounts = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const appliedDiscount = discounts.find((discount) => discount && discount.applicable !== false);
      const appliedCode = appliedDiscount ? String(appliedDiscount.code || '').trim() : '';
      const matchingCard = this.cards.find((card) =>
        String(card.dataset.discountCode || '').toUpperCase() === appliedCode.toUpperCase()
      );
      if (matchingCard) {
        this.markSelected(matchingCard);
        this.persistSelection(appliedCode);
      } else {
        this.clearSelection();
        this.persistSelection('');
      }
    }

    persistSelection(code) {
      try {
        if (code) localStorage.setItem(this.storageKey, code);
        else localStorage.removeItem(this.storageKey);
      } catch (error) {}
    }

    async copyPdpCode(button) {
      const code = String(button.dataset.pdpCopyCode || '').trim();
      if (!code || !(await this.copyCode(code))) return;

      if (this.activeCopyButton && this.activeCopyButton !== button) {
        this.activeCopyButton.classList.remove('is-copied');
      }
      this.activeCopyButton = button;
      button.classList.add('is-copied');

      window.clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = window.setTimeout(() => {
        button.classList.remove('is-copied');
        if (this.activeCopyButton === button) this.activeCopyButton = null;
      }, 1600);
    }

    async copyCode(code) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(code);
          return true;
        }

        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        textarea.remove();
        return copied;
      } catch (error) {
        return false;
      }
    }

    showStatus(message, state) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-success', state === 'success');
      this.status.classList.toggle('is-error', state === 'error');
    }
  }

  customElements.define('product-discount-offers', ProductDiscountOffers);
})();
